/**
 * Component catalog — single source of truth for generative UI component schemas.
 *
 * Defines all dashboard components with Zod prop schemas. Used by:
 *   - React components (import prop types via z.infer)
 *   - Build script (generates catalog.md for the chatbot skill)
 *   - Runtime validation (catalog.validate())
 *   - defineRegistry (typed component functions with { props, children, bindings })
 *
 * Uses the React schema from @json-render/react/schema which provides:
 *   - `slots` (replaces hasChildren) for child element support
 *   - `visible` condition on elements for state-driven visibility
 *   - `$bindState` expressions in props for two-way state binding
 */

import { defineCatalog, defineSchema } from '@json-render/core';
import { shadcnComponentDefinitions } from '@json-render/shadcn/catalog';
import { z } from 'zod';

// =============================================================================
// Shared prop schemas — reused by chart components
// =============================================================================

const dataProps = {
  data: z
    .array(z.record(z.string(), z.unknown()))
    .optional()
    .describe('Array of data points (use this OR dataUrl)'),
  dataUrl: z
    .string()
    .optional()
    .describe('`chatbot-data://<file>.json` for large datasets'),
};

const chartBaseProps = {
  ...dataProps,
  xKey: z.string(),
  yKeys: z.array(z.string()),
  colors: z.array(z.string()).optional().describe('Hex colors per series'),
  height: z.number().optional().describe('Chart height in px (default 300)'),
  title: z.string().optional().describe('Chart title'),
};

// =============================================================================
// Prop schemas — exported for use by React components via z.infer
// =============================================================================

// Stat-card props. Diverges from upstream `@json-render/shadcn` Card by adding
// an explicit `value` prop so a KPI's headline metric can be authored directly
// in JSON without nesting a child `Heading`. The Card has three text slots
// stacked top-to-bottom in the header, each with deliberately different weight:
//
//   title       → small muted caption (label above the headline)
//   value       → LARGE DARK headline metric (the loud number)
//   description → small muted caption (subtext below the headline)
//
// Child elements (default slot) render in the card body below the header.
// The `.describe()` text on each prop is the LLM-facing contract — it shows up
// in the generated `catalog.md` and `catalog.schema.json`, so keep it pointed
// at spec authors (the chatbot), not React engineers.
export const CardProps = z.object({
  title: z
    .string()
    .optional()
    .describe('Small muted KPI label rendered ABOVE the value (e.g. "Total PnL"). This is the caption slot for a stat card — NOT a section heading for a chart or table.'),
  value: z
    .string()
    .optional()
    .describe('Large dark headline rendered in the middle of the card (e.g. "$2,640", "21.7%"). The KPI metric itself — every Card should have this set.'),
  description: z
    .string()
    .optional()
    .describe('Small muted KPI subtext rendered BELOW the value (e.g. "1 lot · YTD"). For context/units only.'),
  // Typed as z.string() because the repo is on TS 4.9 and Zod v4's z.enum
  // declaration uses a `const` type parameter (TS 5.0+); the describe() text
  // carries the contract for the LLM and runtime consumers validate the value.
  maxWidth: z
    .string()
    .optional()
    .describe('Max width of the card: "sm" | "md" | "lg" | "full". Defaults to "full".'),
  centered: z
    .boolean()
    .optional()
    .describe('Center the card horizontally (mx-auto). Defaults to false.'),
});

const lineStylesSchema = z
  .record(z.string(), z.string())
  .optional()
  .describe('Map of yKey to line style ("solid" | "dashed" | "dotted"). Unlisted keys default to "solid".');

// Y-axis domain bound: a number, or 'auto' | 'dataMin' | 'dataMax' for Recharts.
// Typed as z.unknown() because the repo is on TS 4.9 and Zod v4's z.union/z.enum
// declarations use `const` type parameters (TS 5.0+); the describe() text
// carries the contract for the LLM and Recharts validates shapes at runtime.
const yDomainBound = z.unknown();

export const LineChartProps = z.object({
  ...chartBaseProps,
  xKey: z.string().describe('Key for x-axis'),
  yKeys: z.array(z.string()).describe('Keys for y-axis series'),
  yAxisLabel: z.string().optional().describe('Y-axis label'),
  showGrid: z.boolean().optional().describe('Show grid lines (default true)'),
  lineStyles: lineStylesSchema,
  yDomain: z
    .tuple([yDomainBound, yDomainBound])
    .optional()
    .describe('Y-axis domain as [min, max]. Each bound is a number or "auto" | "dataMin" | "dataMax". Defaults to ["auto", "auto"].'),
  xAxisShowAllTicks: z
    .boolean()
    .optional()
    .describe('Force every x-axis tick label to render instead of letting Recharts auto-skip labels that would overlap (default false).'),
});

export const BarChartProps = z.object({
  ...chartBaseProps,
  xKey: z.string().describe('Key for x-axis categories'),
  yKeys: z.array(z.string()).describe('Keys for bar values'),
  stacked: z
    .boolean()
    .optional()
    .describe(
      'Stack bars (default false). Stacks are diverging: positive values stack '
      + 'upward from zero and negative values stack downward, so mixed-sign '
      + 'series (e.g. YoY change with both up and down components) remain '
      + 'individually visible instead of one color absorbing the others.',
    ),
  yDomain: z.tuple([z.number(), z.number()]).optional().describe('Fixed Y-axis [min, max] domain, e.g. for a zoom/brush control (default auto)'),
});

export const AreaChartProps = z.object({
  ...chartBaseProps,
  xKey: z.string().describe('Key for x-axis'),
  yKeys: z.array(z.string()).describe('Keys for area series'),
  stacked: z
    .boolean()
    .optional()
    .describe(
      'Stack areas (default false). Stacks are diverging: positive values '
      + 'stack upward from zero and negative values stack downward.',
    ),
  gradient: z.boolean().optional().describe('Gradient fill (default true)'),
  lineStyles: lineStylesSchema,
});

export const ScatterChartProps = z.object({
  ...chartBaseProps,
  xKey: z.string().describe('Key for x-axis values'),
  yKeys: z.array(z.string()).describe('Keys for y-axis series'),
});

export const TradingViewChartProps = z.object({
  symbol: z
    .string()
    .describe(
      'Symbol resolved by backend instrument search or the analyzing-prices skill (e.g., "CME:CL.M1!", "ICE:B.M1!"). Must use our EXCHANGE:PRODUCT.CONTRACT format — do not guess or use TradingView\'s default symbols.',
    ),
  interval: z
    .string()
    .optional()
    .describe(
      'Initial resolution. Daily/weekly/monthly: "D" (default), "1W", "1M". Intraday (requires showIntraday=true): "1", "5", "15", "30", "60", "120", "240".',
    ),
  height: z.number().optional().describe('Chart height in px (default 500)'),
  title: z.string().optional().describe('Chart title'),
  showIntraday: z.boolean().optional().describe('Enable intraday resolutions (default false)'),
  chartType: z
    .string()
    .optional()
    .describe(
      'Initial chart style. Either "line" (default) or "candles". Other TradingView chart types are intentionally not exposed.',
    ),
  // Typed as z.unknown() because the repo is on TS 4.9 and Zod v4's z.union
  // declarations use `const` type parameters (TS 5.0+); the describe() text
  // carries the contract for the LLM and the component validates shape at runtime.
  dateRange: z
    .unknown()
    .optional()
    .describe(
      'Initial visible window. Either a relative string ("1D", "5D", "1M", "3M", "6M", "1Y", "5Y", "YTD", "ALL") or an object { from: "YYYY-MM-DD", to: "YYYY-MM-DD" }. Omit for TradingView default.',
    ),
  studies: z
    .array(
      z.object({
        name: z
          .string()
          .describe(
            'Study name exactly as TradingView expects, e.g. "Moving Average", "Moving Average Exponential", "Relative Strength Index", "MACD", "Bollinger Bands", "Volume".',
          ),
        inputs: z
          .record(z.string(), z.unknown())
          .optional()
          .describe('Study inputs, e.g. { length: 20, source: "close" }.'),
        overrides: z
          .record(z.string(), z.unknown())
          .optional()
          .describe('Display overrides, e.g. { "plot.color": "#2962FF", "plot.linewidth": 2 }.'),
        forceOverlay: z
          .boolean()
          .optional()
          .describe('If true, render on the main price pane instead of a separate sub-pane (default false).'),
      }),
    )
    .optional()
    .describe('Technical indicators to pre-load when the chart opens.'),
  comparedTrades: z
    .array(
      z.object({
        formula: z
          .string()
          .describe(
            'Symbol or formula to overlay on the main price pane, resolved by the backend or analyzing-prices skill (e.g. "ICE:B.M1!", "CME:CL.M1! - CME:CL.M2!"). Same EXCHANGE:PRODUCT.CONTRACT format as `symbol`.',
          ),
        short_name: z
          .string()
          .optional()
          .describe('Optional short label for legend display; defaults to the formula text.'),
        color: z
          .string()
          .optional()
          .describe('Optional hex color (e.g. "#FF9800"). Omit to auto-assign from the shared overlay palette.'),
      }),
    )
    .optional()
    .describe(
      'Additional symbols to overlay on the main pane for comparison. Use when the user asks to compare two or more instruments on one chart. The primary `symbol` drives the scale; overlays are rendered as TradingView Overlay studies. Example: symbol="CME:CL.M1!", comparedTrades=[{formula: "ICE:B.M1!"}].',
    ),
});

// Shared description for symbol-only chart components (seasonality, forward curve,
// percentile). These components fetch chart data internally via /trades?formula=...
// — they accept anything the formula parser takes, so single symbols and formula
// expressions both work.
const symbolChartSymbolDescription =
  'Symbol or formula resolved by the backend or analyzing-prices skill (e.g., "CME:CL.M1!", '
  + '"ICE:B.M1!", or a formula like "CME:CL.M1! - CME:CL.M2!"). Must use our '
  + 'EXCHANGE:PRODUCT.CONTRACT format — do not guess or use TradingView\'s default symbols.';

export const SeasonalityChartProps = z.object({
  symbol: z.string().describe(symbolChartSymbolDescription),
  height: z.number().optional().describe('Chart height in px (default 500)'),
  title: z.string().optional().describe('Chart title'),
});

export const ForwardCurveChartProps = z.object({
  symbol: z.string().describe(symbolChartSymbolDescription),
  height: z.number().optional().describe('Chart height in px (default 500)'),
  title: z.string().optional().describe('Chart title'),
  frequency: z
    .string()
    .optional()
    .describe('Historical curve frequency — "D" | "W" | "M". Used with `periods`.'),
  periods: z
    .number()
    .optional()
    .describe('Number of historical curves to fetch. Used with `frequency`.'),
  dates: z
    .array(z.string())
    .optional()
    .describe('Explicit ISO dates (YYYY-MM-DD) for historical curves. Overrides frequency/periods.'),
});

export const PercentileChartProps = z.object({
  symbol: z.string().describe(symbolChartSymbolDescription),
  height: z.number().optional().describe('Chart height in px (default 500)'),
  title: z.string().optional().describe('Chart title'),
});

export const DaysToExpiryChartProps = z.object({
  symbol: z.string().describe(symbolChartSymbolDescription),
  height: z.number().optional().describe('Chart height in px (default 500)'),
  title: z.string().optional().describe('Chart title'),
});

export const FundamentalsSeasonalityChartProps = z.object({
  ...dataProps,
  dateKey: z
    .string()
    .optional()
    .describe('Field on each row holding an ISO date "YYYY-MM-DD" (default "date")'),
  valueKey: z
    .string()
    .optional()
    .describe('Field on each row holding the numeric value (default "value")'),
  frequency: z
    .string()
    .optional()
    .describe('Sampling frequency — "D" daily, "W" weekly, "M" monthly. If omitted, inferred from the median date spacing.'),
  title: z.string().optional().describe('Chart title'),
  subtitle: z
    .string()
    .optional()
    .describe('Optional caption shown under the title (e.g. "EIA weekly · latest 24 Apr 2026")'),
  yAxisLabel: z.string().optional().describe('Y-axis label (e.g. "Thousand barrels per day")'),
  yDomain: z
    .tuple([yDomainBound, yDomainBound])
    .optional()
    .describe(
      'Y-axis domain as [min, max]. Each bound is a number or a Recharts keyword — "auto", "dataMin", '
      + '"dataMax", or an expression like "dataMin - 5". Omit to fit the axis to the data range (the default); '
      + 'pass [0, "auto"] to anchor the axis at 0.',
    ),
  height: z.number().optional().describe('Chart height in px (default 360)'),
  currentYear: z
    .number()
    .optional()
    .describe('Override which year is treated as "current" (defaults to the latest year in the data).'),
  visibleYears: z
    .array(z.number())
    .optional()
    .describe(
      'Years shown by default in the chart (e.g. [2026, 2025, 2024]). Years outside this list are still '
      + 'listed in the legend and toggleable, but start hidden. Defaults to the three most recent years. '
      + 'Does not affect the band/avg, which always use up to 5 prior years from the data.',
    ),
  yearColors: z
    .record(z.string(), z.string())
    .optional()
    .describe(
      'Per-year line color overrides keyed by year, e.g. {"2026": "#000000"}. Only pass when the user '
      + 'asks for specific colors — the default already follows the house convention (current year black, '
      + 'prior year red, the year before green).',
    ),
});

export const DataTableProps = z.object({
  columns: z
    .array(
      z.object({
        key: z.string(),
        label: z.string(),
        align: z.string().optional().describe('One of "left", "center", "right" (default "left")'),
      }),
    )
    .describe('Column definitions'),
  ...dataProps,
  title: z.string().optional().describe('Table title'),
  compact: z.boolean().optional().describe('Compact row height'),
  boldLastRow: z.boolean().optional().describe('Bold the last row, e.g. for a grand-total row in a matrix/pivot table'),
  boldLastColumn: z.boolean().optional().describe('Bold the last column, e.g. for a grand-total column in a matrix/pivot table'),
});

export const ShippingMapProps = z.object({
  title: z.string().optional().describe('Map title'),
  height: z.number().optional().describe('Map height in px (default 500)'),

  // Flow layer — trade arcs between countries
  flows: z
    .array(
      z.object({
        origin: z.string().describe('Origin ISO-3166 alpha-3 country code'),
        destination: z.string().describe('Destination ISO-3166 alpha-3 country code'),
        value: z.number().describe('Flow volume (determines line width)'),
        label: z.string().optional().describe('Tooltip label (e.g. "Crude: 1.2M bbl")'),
        group: z.string().optional().describe('Category for color coding (e.g. cargo_type)'),
        originLat: z.number().optional().describe('Known origin port/terminal latitude - used instead of the country centroid when paired with originLon'),
        originLon: z.number().optional().describe('Known origin port/terminal longitude - used instead of the country centroid when paired with originLat'),
        destinationLat: z.number().optional().describe('Known destination port/terminal latitude - used instead of the country centroid when paired with destinationLon'),
        destinationLon: z.number().optional().describe('Known destination port/terminal longitude - used instead of the country centroid when paired with destinationLat'),
      }),
    )
    .optional()
    .describe('Trade flow arcs (inline, or omit and use flowsUrl)'),
  flowsUrl: z.string().optional().describe('`chatbot-data://<file>.json` for flow data'),

  // Vessel layer — ship positions
  vessels: z
    .array(
      z.object({
        lat: z.number().describe('Latitude'),
        lon: z.number().describe('Longitude'),
        name: z.string().optional().describe('Vessel name'),
        imo: z.string().optional().describe('IMO number'),
        speed: z.number().optional().describe('Speed in knots'),
        heading: z.number().optional().describe('Heading in degrees'),
        destination: z.string().optional().describe('Destination port'),
        status: z.string().optional().describe('Navigation status for color coding'),
      }),
    )
    .optional()
    .describe('Vessel positions (inline, or omit and use vesselsUrl)'),
  vesselsUrl: z.string().optional().describe('`chatbot-data://<file>.json` for vessel data'),

  // Route layer — real routed paths (e.g. from a Sea Routes API), distinct
  // from the flow layer's synthetic great-circle arcs
  routes: z
    .array(
      z.object({
        imo: z.string().optional().describe('IMO number of the vessel this route belongs to'),
        coordinates: z
          .array(z.tuple([z.number(), z.number()]))
          .describe('Routed path waypoints as [longitude, latitude] pairs, in order'),
        color: z.string().optional().describe('Hex stroke color (default a muted route color)'),
        label: z.string().optional().describe('Tooltip label (e.g. distance)'),
      }),
    )
    .optional()
    .describe('Routed paths (inline, or omit and use routesUrl)'),
  routesUrl: z.string().optional().describe('`chatbot-data://<file>.json` for route data'),

  // Markers layer — generic positioned points (infrastructure, facilities, events)
  markers: z
    .array(
      z.object({
        lat: z.number().describe('Latitude'),
        lon: z.number().describe('Longitude'),
        name: z.string().optional().describe('Marker label'),
        color: z.string().optional().describe('Hex fill color (default "#2563eb")'),
        size: z.number().optional().describe('Circle radius in px (default 6)'),
        group: z.string().optional().describe('Category for legend grouping'),
        label: z.string().optional().describe('Short text shown next to marker on map'),
        tooltip: z.string().optional().describe('Text shown on hover'),
      }),
    )
    .optional()
    .describe('Positioned point markers (inline, or omit and use markersUrl)'),
  markersUrl: z.string().optional().describe('`chatbot-data://<file>.json` for marker data'),

  // Pie-marker layer — a category-breakdown pie/donut glyph at each point
  // (e.g. cargo-type volumes per region), instead of a single-color circle
  pieMarkers: z
    .array(
      z.object({
        lat: z.number().describe('Latitude'),
        lon: z.number().describe('Longitude'),
        name: z.string().optional().describe('Point label shown in the tooltip header'),
        slices: z
          .array(
            z.object({
              label: z.string().describe('Slice category label'),
              value: z.number().describe('Slice value (determines slice angle and overall glyph size)'),
              color: z.string().optional().describe('Hex color for this slice (default cycles through the flow palette)'),
            }),
          )
          .describe('Category breakdown rendered as pie slices, sized by total value'),
      }),
    )
    .optional()
    .describe('Per-point pie/donut glyphs (inline, or omit and use pieMarkersUrl)'),
  pieMarkersUrl: z.string().optional().describe('`chatbot-data://<file>.json` for pie-marker data'),

  // Choropleth layer — country shading
  choropleth: z
    .array(
      z.object({
        country: z.string().describe('ISO-3166 alpha-3 country code'),
        value: z.number().describe('Value for shading intensity'),
        label: z.string().optional().describe('Tooltip label'),
      }),
    )
    .optional()
    .describe('Country shading values (inline, or omit and use choroplethUrl)'),
  choroplethUrl: z
    .string()
    .optional()
    .describe('`chatbot-data://<file>.json` for choropleth data'),

  // Styling
  colorScheme: z
    .string()
    .optional()
    .describe('Choropleth color scheme: "blues", "reds", "greens", "oranges", or "purples" (default "blues")'),
  flowColors: z
    .record(z.string(), z.string())
    .optional()
    .describe('Map of group name to hex color for flow arcs'),
  highlightedFlowKey: z
    .string()
    .optional()
    .describe(
      'Emphasizes one flow arc (`${origin}-${destination}-${group}`) and dims the rest - for '
      + 'e.g. clicking a row in a companion flows table to spotlight it on the map',
    ),
  projection: z
    .string()
    .optional()
    .describe('Map projection: "naturalEarth", "mercator", or "equalEarth" (default "naturalEarth")'),
  center: z
    .array(z.number())
    .optional()
    .describe('[longitude, latitude] to center the map on (e.g. [49, 26] for Middle East). Requires mercator or equalEarth projection.'),
  scale: z
    .number()
    .optional()
    .describe('Projection scale override. Higher values zoom in (e.g. 1400 for regional, 200 for world). Used with center.'),
});

export const SankeyChartProps = z.object({
  data: z
    .object({
      nodes: z.array(z.object({ name: z.string() })),
      links: z.array(
        z.object({
          source: z.number().describe('Index into the nodes array'),
          target: z.number().describe('Index into the nodes array'),
          value: z.number().describe('Flow volume'),
        }),
      ),
    })
    .optional()
    .describe('Sankey data with nodes[] and links[]. Use this OR dataUrl.'),
  dataUrl: z.string().optional().describe('`chatbot-data://<file>.json` for large datasets'),
  height: z.number().optional().describe('Chart height in px (default 400)'),
  title: z.string().optional().describe('Chart title'),
  nodePadding: z.number().optional().describe('Vertical padding between nodes in px (default 24)'),
  nodeWidth: z.number().optional().describe('Node rectangle width in px (default 10)'),
  colors: z.array(z.string()).optional().describe('Hex colors for nodes'),
});

export const HeatmapProps = z.object({
  // Heatmap accepts either long form OR matrix form. Typed as z.unknown()
  // because TS 4.9 + Zod v4 can't parse z.union (see commit 3e6347ce).
  // Long form (preferred): data is Array<{[xKey]:string,[yKey]:string,[valueKey]:number}>
  //   plus xKey/yKey/valueKey to name those fields.
  // Matrix form: data is number[][] sized yLabels.length x xLabels.length,
  //   plus xLabels (columns) and yLabels (rows). No keys needed.
  // The component validates shape at runtime; describe() carries the contract for the LLM.
  data: z.unknown().optional().describe('Long form: Array<{[xKey],[yKey],[valueKey]}>. Matrix form: number[][] sized yLabels.length x xLabels.length. Use this OR dataUrl.'),
  dataUrl: z.string().optional().describe('`chatbot-data://<file>.json` for large datasets'),
  xKey: z.string().optional().describe('Long form: field on each row used for column headers'),
  yKey: z.string().optional().describe('Long form: field on each row used for row labels'),
  valueKey: z.string().optional().describe('Long form: field on each row used for the numeric cell value'),
  xLabels: z.array(z.string()).optional().describe('Matrix form: column labels (one per column in each data row)'),
  yLabels: z.array(z.string()).optional().describe('Matrix form: row labels (one per row in data)'),
  title: z.string().optional().describe('Chart title'),
  height: z.number().optional().describe('Chart height in px (default 300)'),
  colorScale: z
    .string()
    .optional()
    .describe('Color scale: "blues" | "reds" | "greens" | "oranges" | "diverging" (default "blues"). "diverging" uses blue-white-red for negative-to-positive.'),
  showValues: z.boolean().optional().describe('Show numeric values in cells (default true)'),
});

export const ComboChartProps = z.object({
  ...dataProps,
  xKey: z.string().describe('Key for x-axis'),
  barKeys: z.array(z.string()).describe('Keys rendered as bars'),
  lineKeys: z.array(z.string()).describe('Keys rendered as lines'),
  barColors: z.array(z.string()).optional().describe('Hex colors for bar series'),
  lineColors: z.array(z.string()).optional().describe('Hex colors for line series'),
  stacked: z
    .boolean()
    .optional()
    .describe(
      'Stack bars (default false). Stacks are diverging: positive values '
      + 'stack upward from zero and negative values stack downward.',
    ),
  height: z.number().optional().describe('Chart height in px (default 300)'),
  title: z.string().optional().describe('Chart title'),
  yAxisLabel: z.string().optional().describe('Left y-axis label'),
  rightAxisKeys: z
    .array(z.string())
    .optional()
    .describe('Keys that use a secondary right y-axis (useful when bar and line have different scales)'),
});

// ToggleGroup, Slider, Select — schemas adopted from upstream shadcnComponentDefinitions

// =============================================================================
// Schema + catalog
// =============================================================================

// Schema matching @json-render/react/schema structure (slots instead of
// hasChildren, visible on elements). Uses the default prompt template from
// @json-render/core which auto-generates correct docs for visibility,
// state binding, dynamic props, and components.
export const schema = defineSchema(
  (s) => ({
    spec: s.object({
      root: s.string(),
      elements: s.record(
        s.object({
          type: s.ref('catalog.components'),
          props: s.propsOf('catalog.components'),
          children: s.array(s.string()),
          visible: s.any(),
        }),
      ),
    }),
    catalog: s.object({
      // eslint-disable-next-line lodash/prefer-lodash-method -- s.map() is a schema builder method, not Array.map
      components: s.map({
        props: s.zod(),
        slots: s.array(s.string()),
        description: s.string(),
      }),
    }),
  }),
  {},
);

export const catalog = defineCatalog(schema, {
  components: {
    // Layout — upstream shadcn definitions
    Grid: shadcnComponentDefinitions.Grid,
    Stack: shadcnComponentDefinitions.Stack,

    // Card — local override of upstream shadcn Card. Adds a `value` prop so
    // KPI/stat cards can put their headline metric in a large dark slot
    // without nesting a child Heading element. See CardProps above for the
    // three-slot contract (title = caption, value = headline, description =
    // caption).
    Card: {
      description:
        'KPI stat card for a single metric. Three text slots: '
        + '`title` (small muted label above), `value` (LARGE DARK headline — the metric), '
        + '`description` (small muted subtext below). Do NOT use as a wrapper around charts, '
        + 'tables, maps, or other components — to title those, use the component\'s own `title` '
        + 'prop instead.',
      slots: ['default'],
      props: CardProps,
    },

    // Charts — custom components (no upstream equivalent)
    LineChart: {
      description:
        'Line chart. Provide `data` (inline) or `dataUrl` (S3 reference).',
      slots: [],
      props: LineChartProps,
    },
    BarChart: {
      description:
        'Bar chart. Provide `data` or `dataUrl`.',
      slots: [],
      props: BarChartProps,
    },
    AreaChart: {
      description:
        'Area chart. Provide `data` or `dataUrl`.',
      slots: [],
      props: AreaChartProps,
    },
    ScatterChart: {
      description:
        'Scatter plot. Provide `data` or `dataUrl`.',
      slots: [],
      props: ScatterChartProps,
    },
    TradingViewChart: {
      description:
        'Interactive TradingView Advanced Chart. Use for prices. Pass symbols resolved by the backend or analyzing-prices skill (e.g. `CME:CL.M1!`, `ICE:B.M1!`) — these follow our EXCHANGE:PRODUCT.CONTRACT format, NOT TradingView\'s default symbol universe. Do NOT pass data URLs.',
      slots: [],
      props: TradingViewChartProps,
    },
    SeasonalityChart: {
      description:
        'Seasonality chart — historical year-over-year overlays with 5-year stats (avg/min/max). '
        + 'Pass a symbol or formula resolved by the backend or analyzing-prices skill; the '
        + 'component fetches its own chart data. Do NOT pass `data` or `dataUrl`.',
      slots: [],
      props: SeasonalityChartProps,
    },
    ForwardCurveChart: {
      description:
        'Forward curve chart — contract-by-contract curve with optional historical snapshots. '
        + 'Pass a symbol or formula resolved by the backend or analyzing-prices skill. Optional '
        + '`frequency`+`periods` or explicit `dates` pull historical curves. '
        + 'Do NOT pass `data` or `dataUrl`.',
      slots: [],
      props: ForwardCurveChartProps,
    },
    PercentileChart: {
      description:
        'Percentile chart — cumulative distribution with per-year toggles and the current value highlighted. '
        + 'Pass a symbol or formula resolved by the backend or analyzing-prices skill; the component '
        + 'fetches its own chart data. Do NOT pass `data` or `dataUrl`.',
      slots: [],
      props: PercentileChartProps,
    },
    DaysToExpiryChart: {
      description:
        'Days-to-expiry chart — overlays the past 12 historic vintages, the selected spread, '
        + 'and the next 3 forward vintages on a trading-days-to-expiry x-axis (holiday-aware per '
        + 'exchange calendar). Y is the spread price, X is trading days until the front leg expires '
        + '(rightmost = expiry day). Pass a symbol or formula resolved by the backend or analyzing-prices '
        + 'skill; the component fetches its own chart data. Do NOT pass `data` or `dataUrl`.',
      slots: [],
      props: DaysToExpiryChartProps,
    },
    FundamentalsSeasonalityChart: {
      description:
        'Seasonality chart for non-price (fundamentals) data — inventories, exports, demand, freight, etc. '
        + 'Plots each calendar year as its own line on a Jan-to-Dec axis with a 5-year min/max band and '
        + '5-year average overlaid (historical years only — current year is excluded from the band/avg). '
        + 'Lines follow the house year-color convention by default (current year black, prior year red, '
        + 'the year before green) — override individual years via `yearColors` only when asked. '
        + 'Default-visible series are the three most recent years (override via `visibleYears`); older years are toggleable from the legend. '
        + 'Provide rows of `{ date, value }` via `data` or `dataUrl`. The axis labels match the sampling '
        + 'frequency: months for daily/monthly data, ISO week numbers for weekly data. '
        + 'Use this for fundamentals — for prices, prefer `SeasonalityChart` (symbol-driven). '
        + 'ALWAYS use one of these two components for year-over-year seasonal comparisons — never rebuild '
        + 'seasonality as a generic `LineChart` with per-year columns.',
      slots: [],
      props: FundamentalsSeasonalityChartProps,
    },
    DataTable: {
      description: 'Data table with columns. Provide `data` or `dataUrl`.',
      slots: [],
      props: DataTableProps,
    },

    // Maps — geographic visualization
    ShippingMap: {
      description:
        'World map for maritime oil trade visualization with three optional layers: ' +
        'flow arcs (trade routes between countries with width by volume), ' +
        'vessel markers (live ship positions), and choropleth (country shading by value). ' +
        'Use ISO-3166 alpha-3 country codes. Provide layer data inline or via `chatbot-data://` URLs.',
      slots: [],
      props: ShippingMapProps,
    },

    SankeyChart: {
      description:
        'Sankey diagram. ' +
        'Data format: `{ nodes: [{ name }], links: [{ source, target, value }] }` where source/target are node indices.',
      slots: [],
      props: SankeyChartProps,
    },
    Heatmap: {
      description:
        'Heatmap grid. Two accepted shapes: ' +
        '(1) Long form (preferred): data is Array<{[xKey],[yKey],[valueKey]}> pivoted into rows (yKey) x columns (xKey) with cell color from valueKey. ' +
        '(2) Matrix form: data is number[][] sized yLabels.length x xLabels.length.',
      slots: [],
      props: HeatmapProps,
    },
    ComboChart: {
      description:
        'Combined bar + line chart. ' +
        'Use `rightAxisKeys` when bar and line series have different scales.',
      slots: [],
      props: ComboChartProps,
    },

    // Interactive — upstream shadcn schemas
    ToggleGroup: shadcnComponentDefinitions.ToggleGroup,
    Slider: shadcnComponentDefinitions.Slider,
    Select: shadcnComponentDefinitions.Select,

    // Display — upstream shadcn schemas, rendered with our components/ui primitives
    Tabs: shadcnComponentDefinitions.Tabs,
    Accordion: shadcnComponentDefinitions.Accordion,
    Collapsible: shadcnComponentDefinitions.Collapsible,
    Text: shadcnComponentDefinitions.Text,
    Heading: shadcnComponentDefinitions.Heading,
    Badge: shadcnComponentDefinitions.Badge,
    Alert: shadcnComponentDefinitions.Alert,
    Progress: shadcnComponentDefinitions.Progress,
  },
});
